import {
  BOLETO_MOCK,
  currentAmount,
  planTags,
  reduceInvoicesByStatus
} from './utils';
import { Box, Button, Stack, Text } from '@qcx/ui';
import { CanAccess } from '@refinedev/core';
import { Col, Row, Tabs, Typography } from 'antd';
import { IMoney } from 'interfaces/money';
import { IOrderPaymentPlansDetail } from 'interfaces/orders';
import { InvoiceTable } from './InvoiceTable';
import { formatMoney } from 'services/money';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

const { Title } = Typography;

export const PlanContentTable = ({
  orderId,
  plan
}: {
  orderId: string;
  plan: IOrderPaymentPlansDetail;
}) => {
  const [selectedInvoices, setSelectedInvoices] = useState<
    { id: string; index: number; amount: IMoney; planName?: string }[]
  >([]);

  const navigate = useNavigate();

  const setInvoiceSelection = (
    shoudlSelect: boolean,
    i: IOrderPaymentPlansDetail['invoices'][number]
  ) => {
    const isSelected = selectedInvoices.find(({ id }) => id === i.id);

    if (isSelected && !shoudlSelect) {
      const newSelected = selectedInvoices.filter(({ id }) => id !== i.id);
      setSelectedInvoices(newSelected);
      return;
    }

    if (shoudlSelect && !isSelected) {
      setSelectedInvoices([
        ...selectedInvoices,
        { id: i.id, index: i.index + 1, amount: i.amount }
      ]);
    }
  };

  const planInvoices = reduceInvoicesByStatus(plan.invoices);
  const pendingInvoices = (planInvoices['due'] || [])
    ?.concat(planInvoices['initial'] || [])
    ?.concat(planInvoices['cancelled'] || [])
    ?.sort((a, b) => a.index - b.index);

  const createOrderDeal = () => {
    navigate(`/payment_deals/payment_plans/create?id=${orderId}`, {
      state: {
        planDealData: {
          invoice_ids: selectedInvoices.map(({ id }) => id),
          amount: currentAmount(selectedInvoices),
          preferences: {
            kind: plan?.preferences?.kind,
            info: plan?.preferences?.info,
            gateway_config: plan?.preferences?.gateway_config,
            installments: BOLETO_MOCK
          },
          installments: plan?.invoices.length
        },
        initialPlanData: {
          debitInvoicesCount: selectedInvoices.length,
          debitAmount: currentAmount(selectedInvoices),
          paymentKind: plan.preferences.kind,
          totalInvoices: plan.invoices.length,
          planValue: plan.amount,
          selectedInstallments: selectedInvoices
            ?.map(inv => inv.index)
            ?.join(', ')
        }
      }
    });
  };

  return (
    <>
      <Stack
        css={{
          p: '$5',
          w: '$full',
          flexWrap: 'wrap',
          '@lg': { flexWrap: 'nowrap' }
        }}
      >
        <div className="border rounded-lg p-2 mb-2 w-full">
          <Row>
            {planTags(plan).map(item => (
              <Col key={item.title} md={4} style={{ margin: 10 }}>
                <Title level={5}>{item.title}</Title>

                <div className="flex gap-2">
                  <Text>{item.text ? `${item.text}` : '-'}</Text>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </Stack>
      <Box css={{ p: '$5', width: '100%' }}>
        <Tabs style={{ width: '100%' }}>
          <Tabs.TabPane
            style={{ width: '100%' }}
            tab="Parcelas pendentes"
            key="due-invoices"
          >
            <Stack
              flow={{ '@initial': 'column', '@xl': 'row' }}
              css={{
                flexWrap: 'wrap',
                w: '$full',
                gap: '$5',
                '@xl': { flexWrap: 'nowrap' }
              }}
            >
              <InvoiceTable
                data={pendingInvoices}
                onSelect={setInvoiceSelection}
                showPayments
                orderId={orderId}
                preferences={plan.preferences}
                plan={plan}
              />
              <Box css={{ w: '100%', '@lg': { maxW: '384px' } }}>
                <Stack
                  flow="column"
                  css={{
                    rounded: '$lg',
                    bgColor: '$neutral100',
                    p: '$6',
                    gap: '$6',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box>
                    <Stack
                      css={{
                        whiteSpace: 'nowrap',
                        justifyContent: 'space-between',
                        gap: '$4'
                      }}
                    >
                      <Text
                        variant="xs"
                        css={{ d: 'block', color: '$textMuted', mb: '$2' }}
                      >
                        Selecionadas
                      </Text>
                      <Text
                        weight="semibold"
                        variant="xs"
                        css={{ whiteSpace: 'initial' }}
                      >
                        {selectedInvoices.length === 0
                          ? 'Nenhuma'
                          : selectedInvoices
                              .map(({ index }) => `${index}`)
                              .join(', ')}
                      </Text>
                    </Stack>
                    <Stack
                      css={{
                        whiteSpace: 'nowrap',
                        justifyContent: 'space-between',
                        gap: '$4'
                      }}
                    >
                      <Text
                        variant="xs"
                        css={{ d: 'block', color: '$textMuted', mb: '$2' }}
                      >
                        Valor
                      </Text>
                      <Text weight="semibold" variant="xs" css={{ d: 'block' }}>
                        {selectedInvoices.length === 0
                          ? '-'
                          : formatMoney(
                              currentAmount(selectedInvoices) as IMoney
                            )}
                      </Text>
                    </Stack>
                  </Box>

                  <CanAccess resource="payment_plans" action="deal">
                    <Button
                      variant="primary"
                      disabled={selectedInvoices.length === 0}
                      onClick={createOrderDeal}
                    >
                      Renegociar parcelas
                    </Button>
                  </CanAccess>
                </Stack>
              </Box>
            </Stack>
          </Tabs.TabPane>
          <Tabs.TabPane
            style={{ width: '100%' }}
            tab="Parcelas pagas"
            key="paid-invoices"
          >
            <Stack
              flow={{ '@initial': 'column', '@xl': 'row' }}
              css={{
                flexWrap: 'wrap',
                w: '$full',
                gap: '$5',
                '@xl': { flexWrap: 'nowrap' }
              }}
            >
              <InvoiceTable
                data={planInvoices['paid']?.sort((a, b) => a.index - b.index)}
                orderId={orderId}
                preferences={plan.preferences}
                plan={plan}
              />
              <Box css={{ w: '100%', '@lg': { maxW: '384px' } }}>
                <Stack
                  flow="column"
                  css={{
                    rounded: '$lg',
                    bgColor: '$neutral100',
                    p: '$6',
                    gap: '$6',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box>
                    <Stack
                      css={{
                        whiteSpace: 'nowrap',
                        justifyContent: 'space-between',
                        gap: '$4'
                      }}
                    >
                      <Text
                        variant="xs"
                        css={{ d: 'block', color: '$textMuted', mb: '$2' }}
                      >
                        Parcelas pagas
                      </Text>
                      <Text
                        weight="semibold"
                        variant="xs"
                        css={{ whiteSpace: 'initial' }}
                      >
                        {!planInvoices['paid'] ||
                        planInvoices['paid']?.length === 0
                          ? 'Nenhuma'
                          : planInvoices['paid']
                              ?.map(({ index }) => `${index}`)
                              .join(', ')}
                      </Text>
                    </Stack>
                    <Stack
                      css={{
                        whiteSpace: 'nowrap',
                        justifyContent: 'space-between',
                        gap: '$4'
                      }}
                    >
                      <Text
                        variant="xs"
                        css={{ d: 'block', color: '$textMuted', mb: '$2' }}
                      >
                        Valor pago
                      </Text>
                      <Text weight="semibold" variant="xs" css={{ d: 'block' }}>
                        {!planInvoices['paid'] ||
                        planInvoices['paid']?.length === 0
                          ? '-'
                          : formatMoney(
                              currentAmount(planInvoices['paid']) as IMoney
                            )}
                      </Text>
                    </Stack>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          </Tabs.TabPane>
          <Tabs.TabPane
            style={{ width: '100%' }}
            tab="Parcelas renegociadas"
            key="dealed-invoices"
          >
            <Stack
              flow={{ '@initial': 'column', '@xl': 'row' }}
              css={{
                w: '$full',
                gap: '$5',
                flexWrap: 'wrap',
                '@xl': { flexWrap: 'nowrap' }
              }}
            >
              <InvoiceTable
                data={planInvoices['dealed']?.sort((a, b) => a.index - b.index)}
                orderId={orderId}
                preferences={plan.preferences}
                plan={plan}
              />
              <Box css={{ w: '100%', '@lg': { maxW: '384px' } }}>
                <Stack
                  flow="column"
                  css={{
                    rounded: '$lg',
                    bgColor: '$neutral100',
                    p: '$6',
                    gap: '$6',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box>
                    <Stack
                      css={{
                        whiteSpace: 'nowrap',
                        justifyContent: 'space-between',
                        gap: '$4'
                      }}
                    >
                      <Text
                        variant="xs"
                        css={{ d: 'block', color: '$textMuted', mb: '$2' }}
                      >
                        Parcelas renegociadas
                      </Text>
                      <Text
                        weight="semibold"
                        variant="xs"
                        css={{ whiteSpace: 'initial' }}
                      >
                        {!planInvoices['dealed'] ||
                        planInvoices['dealed']?.length === 0
                          ? 'Nenhuma'
                          : planInvoices['dealed']
                              ?.sort((a, b) => a.index - b.index)
                              .map(({ index }) => `${index}`)
                              .join(', ')}
                      </Text>
                    </Stack>
                    <Stack
                      css={{
                        whiteSpace: 'nowrap',
                        justifyContent: 'space-between',
                        gap: '$4'
                      }}
                    >
                      <Text
                        variant="xs"
                        css={{ d: 'block', color: '$textMuted', mb: '$2' }}
                      >
                        Valor Renegociado
                      </Text>
                      <Text weight="semibold" variant="xs" css={{ d: 'block' }}>
                        {!planInvoices['dealed'] ||
                        planInvoices['dealed']?.length === 0
                          ? '-'
                          : formatMoney(
                              currentAmount(planInvoices['dealed']) as IMoney
                            )}
                      </Text>
                    </Stack>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          </Tabs.TabPane>
        </Tabs>
      </Box>
    </>
  );
};
